// src/components/TradeListings.jsx

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

import { isTradeAvailable } from "../utils/tradeAvailability";
import "./TradeListings.css";

import { getTradeImageSrc, handleImgError } from "../utils/images";

const sponsoredAnimStyle = (index) => ({
  animationDelay: `${Math.min(index * 70, 420)}ms`,
});

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.toDate === "function") return ts?.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    return 0;
  } catch {
    return 0;
  }
};

const formatDate = (ts, mode = "long") => {
  const ms = toMillisSafe(ts);
  if (!ms) return "";

  const opts =
    mode === "short"
      ? { day: "numeric", month: "short", year: "numeric" }
      : { day: "numeric", month: "long", year: "numeric" };

  return new Date(ms).toLocaleDateString("en-GB", opts);
};

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const safeLower = (v) => String(v ?? "").trim().toLowerCase();

const getConditionKey = (trade) => {
  const raw =
    trade?.condition ??
    trade?.itemCondition ??
    trade?.conditionLabel ??
    trade?.conditionNorm ??
    (typeof trade?.isNew === "boolean" ? (trade.isNew ? "new" : "used") : "") ??
    "";

  const k = safeLower(raw);
  if (!k) return "";
  if (k === "new" || k.includes("brand new")) return "new";
  if (k === "used" || k.includes("pre-owned") || k.includes("preowned") || k.includes("second")) return "used";
  if (["good", "fair", "poor", "ok", "okay", "excellent"].includes(k)) return "used";
  return k;
};

const normalizeCategoryValue = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "";

  const normalized = s
    .replace(/&/g, "and")
    .replace(/[/,]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

  const aliases = {
    books_and_media: "books_media",
    health_and_fitness: "health_fitness",
    home_and_garden: "home_garden",
    art_and_collectibles: "art_collectibles",
    food_and_dining: "food_dining",
    business_and_industrial: "business_industrial",
    pets_and_animals: "pets_animals",
    travel_and_experiences: "travel_experiences",
    jobs_and_services: "jobs_services",
    sports_and_outdoors: "sports_outdoors",
    baby_and_kids: "baby_kids",
  };

  return aliases[normalized] || normalized;
};

const getTradeCategoryKey = (trade) => {
  const directKey = normalizeCategoryValue(trade?.categoryKey);
  if (directKey) return directKey;

  const fromCategory = normalizeCategoryValue(trade?.category);
  if (fromCategory) return fromCategory;

  const fromLabel = normalizeCategoryValue(trade?.categoryLabel);
  if (fromLabel) return fromLabel;

  return "";
};

const matchesCategoryFilter = (trade, categoryKey) => {
  const wanted = normalizeCategoryValue(categoryKey);
  if (!wanted) return true;

  const tradeKey = getTradeCategoryKey(trade);
  if (!tradeKey) return false;

  return tradeKey === wanted;
};

const matchesCondition = (trade, cond) => {
  const c = safeLower(cond);
  if (!c || c === "all") return true;
  const key = getConditionKey(trade);
  if (!key) return false;
  return key === c;
};

const getTradePreference = () => {
  return "cash_only";
};

const preferenceLabel = () => {
  return { text: "Cash offer", tone: "tradepref-cash" };
};

const fallbackSvgDataUri = (title = "Trade Item") => {
  const safe = String(title).slice(0, 28);
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="600" height="400">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#eef2ff"/>
        <stop offset="1" stop-color="#f8fafc"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#g)"/>
    <circle cx="90" cy="110" r="44" fill="#dbeafe"/>
    <rect x="160" y="90" width="360" height="34" rx="10" fill="#e5e7eb"/>
    <rect x="160" y="140" width="280" height="26" rx="10" fill="#eef2f7"/>
    <text x="50%" y="78%" font-family="Arial, sans-serif" font-size="26" text-anchor="middle" fill="#0f172a" font-weight="700">
      ${safe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}
    </text>
    <text x="50%" y="86%" font-family="Arial, sans-serif" font-size="14" text-anchor="middle" fill="#64748b">
      No image provided
    </text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getPrimaryImage = (trade) => {
  const candidates =
    trade?.imageURLs ??
    trade?.imageUrls ??
    trade?.images ??
    trade?.imageURL ??
    trade?.imageUrl ??
    null;

  if (Array.isArray(candidates) && candidates.length) return candidates[0];
  if (typeof candidates === "string" && candidates.trim()) return candidates.trim();
  return null;
};

const getTradeImages = (trade) => {
  const candidates = [
    ...(Array.isArray(trade?.imageURLs) ? trade.imageURLs : []),
    ...(Array.isArray(trade?.imageUrls) ? trade.imageUrls : []),
    ...(Array.isArray(trade?.images) ? trade.images : []),
    ...(typeof trade?.imageURL === "string" ? [trade.imageURL] : []),
    ...(typeof trade?.imageUrl === "string" ? [trade.imageUrl] : []),
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return Array.from(new Set(candidates));
};

const matchesSearchStrong = (trade, term) => {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return 0;

  let score = 0;
  const title = String(trade?.title || "").toLowerCase();
  const desc = String(trade?.description || "").toLowerCase();
  const brand = String(trade?.brand || "").toLowerCase();
  const category = String(
    trade?.categoryLabel || trade?.category || trade?.categoryKey || ""
  ).toLowerCase();

  if (title.includes(q)) score += 6;
  if (brand.includes(q)) score += 3;
  if (category.includes(q)) score += 2;
  if (desc.includes(q)) score += 2;

  return score;
};

const getFreshnessScore = (trade) => {
  const created = toMillisSafe(trade?.createdAt);
  if (!created) return 0;
  const ageHours = (Date.now() - created) / (1000 * 60 * 60);

  if (ageHours <= 24) return 5;
  if (ageHours <= 72) return 4;
  if (ageHours <= 7 * 24) return 3;
  if (ageHours <= 14 * 24) return 2;
  return 1;
};

const matchesCategoryStrong = (trade, category) => {
  const wanted = normalizeCategoryValue(category);
  if (!wanted) return 0;
  return getTradeCategoryKey(trade) === wanted ? 3 : 0;
};

const rankRegularTrades = (list, { searchTerm = "", categoryKey = "" } = {}) => {
  return [...list].sort((a, b) => {
    const scoreA =
      matchesSearchStrong(a, searchTerm) +
      matchesCategoryStrong(a, categoryKey) +
      getFreshnessScore(a) +
      (moneyZAR(a?.expectedCashAmount ?? a?.estimatedValue ?? a?.price) ? 2 : 0) +
      Math.min(getTradeImages(a).length, 4) * 0.4;

    const scoreB =
      matchesSearchStrong(b, searchTerm) +
      matchesCategoryStrong(b, categoryKey) +
      getFreshnessScore(b) +
      (moneyZAR(b?.expectedCashAmount ?? b?.estimatedValue ?? b?.price) ? 2 : 0) +
      Math.min(getTradeImages(b).length, 4) * 0.4;

    if (scoreB !== scoreA) return scoreB - scoreA;
    return toMillisSafe(b?.createdAt) - toMillisSafe(a?.createdAt);
  });
};

const normalizeForUI = (t) => {
  const images =
    Array.isArray(t?.imageURLs) ? t.imageURLs :
    Array.isArray(t?.images) ? t.images :
    Array.isArray(t?.imageUrls) ? t.imageUrls :
    typeof t?.imageURL === "string" ? [t.imageURL] :
    typeof t?.imageUrl === "string" ? [t.imageUrl] :
    [];

  const cash =
    t?.expectedCashAmount ??
    t?.cashExpected ??
    t?.expectedCash ??
    t?.estimatedValue ??
    t?.price ??
    null;

  const resolvedCategoryLabel = t?.categoryLabel ?? t?.category ?? null;
  const resolvedCategoryKey =
    normalizeCategoryValue(t?.categoryKey) ||
    normalizeCategoryValue(t?.category) ||
    normalizeCategoryValue(t?.categoryLabel) ||
    null;

  return {
    ...t,
    imageURLs: images,
    expectedCashAmount: cash,
    categoryLabel: resolvedCategoryLabel,
    categoryKey: resolvedCategoryKey,
  };
};

const matchesSearch = (trade, term) => {
  if (!term) return true;
  const q = String(term).trim().toLowerCase();
  if (!q) return true;

  const fields = [
    trade?.title,
    trade?.description,
    trade?.condition,
    trade?.categoryLabel,
    trade?.category,
    trade?.categoryKey,
    trade?.brand,
    trade?.location?.city,
    trade?.location?.region,
    trade?.location?.country,
    trade?.location?.label,
  ];

  return fields.some((v) => String(v ?? "").toLowerCase().includes(q));
};

const getPromotionStatus = (trade) =>
  trade?.promotionStatus && typeof trade.promotionStatus === "object"
    ? trade.promotionStatus
    : {};

const getRanking = (trade) =>
  trade?.ranking && typeof trade.ranking === "object"
    ? trade.ranking
    : {};

const isFutureTs = (ts) => {
  const ms = toMillisSafe(ts);
  return !!ms && ms > Date.now();
};
const getTopAdDays = (trade) => {
  const promotions = trade?.promotions || {};

  const candidates = [
    promotions?.topAdDays,
    promotions?.sponsoredDays,
    trade?.topAdDays,
    trade?.sponsoredDays,
    promotions?.topAd,
  ];

  for (const value of candidates) {
    if (typeof value === "boolean") continue;

    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return 0;
};

const isSponsoredActive = (trade) => {
  const promotionStatus = getPromotionStatus(trade);
  const ranking = getRanking(trade);
  const promotions = trade?.promotions || {};

  if (promotionStatus?.isSponsored === true) {
    const promotedUntilMs = toMillisSafe(promotionStatus?.promotedUntil);
    if (!promotedUntilMs) return true;
    return promotedUntilMs > Date.now();
  }

  if (ranking?.isSponsored === true) {
    const promotedUntilMs = toMillisSafe(ranking?.promotedUntil);
    if (!promotedUntilMs) return true;
    return promotedUntilMs > Date.now();
  }

  const topAdDays = getTopAdDays(trade);

  const sponsoredFlag =
    trade?.isSponsored === true ||
    trade?.sponsored === true ||
    trade?.isTopAd === true ||
    trade?.topAd === true ||
    promotions?.sponsored === true ||
    promotions?.topAdEnabled === true ||
    topAdDays > 0 ||
    trade?.activeSurfaces?.includes?.("sponsored") ||
    trade?.promotionTypes?.includes?.("top_ad");

  if (!sponsoredFlag) return false;

  const until =
    toMillisSafe(trade?.sponsoredUntil) ||
    toMillisSafe(trade?.topAdUntil) ||
    toMillisSafe(promotions?.sponsoredUntil) ||
    toMillisSafe(promotions?.topAdUntil);

  if (until) return until > Date.now();

  return true;
};

const isHighlightedActive = (trade) => {
  const promotionStatus = getPromotionStatus(trade);
  const promotions = trade?.promotions || {};

  if (promotionStatus?.isSponsored === true && promotionStatus?.highlight === true) {
    const promotedUntilMs = toMillisSafe(promotionStatus?.promotedUntil);
    if (!promotedUntilMs) return true;
    return promotedUntilMs > Date.now();
  }

  const highlightFlag =
    trade?.isHighlighted === true ||
    trade?.highlighted === true ||
    trade?.highlight === true ||
    promotions?.highlight === true ||
    trade?.activeSurfaces?.includes?.("highlighted") ||
    trade?.promotionTypes?.includes?.("highlight");

  const until =
    toMillisSafe(trade?.highlightUntil) ||
    toMillisSafe(promotions?.highlightUntil);

  if (until) return until > Date.now();

  return highlightFlag;
};

const isUrgentActive = (trade) => {
  const promotionStatus = getPromotionStatus(trade);
  const promotions = trade?.promotions || {};

  if (promotionStatus?.isSponsored === true && promotionStatus?.urgent === true) {
    const promotedUntilMs = toMillisSafe(promotionStatus?.promotedUntil);
    if (!promotedUntilMs) return true;
    return promotedUntilMs > Date.now();
  }

  const urgentFlag =
    trade?.urgent === true ||
    trade?.isUrgent === true ||
    promotions?.urgent === true ||
    trade?.activeSurfaces?.includes?.("urgent") ||
    trade?.promotionTypes?.includes?.("urgent");

  const until =
    toMillisSafe(trade?.urgentUntil) ||
    toMillisSafe(promotions?.urgentUntil);

  if (until) return until > Date.now();

  return urgentFlag;
};

const getSponsoredWeight = (trade) => {
  const ranking = getRanking(trade);

  if (Number.isFinite(Number(ranking?.sponsoredWeight))) {
    return Number(ranking.sponsoredWeight);
  }

  const promotions = trade?.promotions || {};
  const topAdDays = getTopAdDays(trade);

  const topAdWeight =
    topAdDays === 31 ? 310 :
    topAdDays === 15 ? 220 :
    topAdDays === 7 ? 140 :
    topAdDays > 0 ? 100 : 0;

  const highlightWeight =
    trade?.isHighlighted ||
    trade?.highlighted ||
    trade?.highlight ||
    promotions?.highlight
      ? 70
      : 0;

  const urgentWeight =
    trade?.isUrgent ||
    trade?.urgent ||
    promotions?.urgent
      ? 25
      : 0;

  return topAdWeight + highlightWeight + urgentWeight;
};

const sortSponsoredTrades = (list) => {
  return [...list].sort((a, b) => {
    const weightDiff = getSponsoredWeight(b) - getSponsoredWeight(a);
    if (weightDiff !== 0) return weightDiff;

    const boostedDiff =
      toMillisSafe(getRanking(b)?.lastBoostedAt || getPromotionStatus(b)?.activatedAt) -
      toMillisSafe(getRanking(a)?.lastBoostedAt || getPromotionStatus(a)?.activatedAt);
    if (boostedDiff !== 0) return boostedDiff;

    return toMillisSafe(b?.createdAt) - toMillisSafe(a?.createdAt);
  });
};

const sortUrgentPriorityTrades = (list) => {
  return [...list].sort((a, b) => {
    // Sponsored urgent trades must appear before urgent-only trades.
    const sponsoredDiff = Number(isSponsoredActive(b)) - Number(isSponsoredActive(a));
    if (sponsoredDiff !== 0) return sponsoredDiff;

    const weightDiff = getSponsoredWeight(b) - getSponsoredWeight(a);
    if (weightDiff !== 0) return weightDiff;

    const urgentUntilDiff =
      toMillisSafe(b?.urgentUntil || b?.promotions?.urgentUntil) -
      toMillisSafe(a?.urgentUntil || a?.promotions?.urgentUntil);

    if (urgentUntilDiff !== 0) return urgentUntilDiff;

    return toMillisSafe(b?.createdAt) - toMillisSafe(a?.createdAt);
  });
};

const isUrgentTrade = (trade) => isUrgentActive(trade);

const isTouchDevice = typeof window !== "undefined" && "ontouchstart" in window;

const TradeCardSkeleton = ({ sponsored = false }) => {
  return (
    <div className={`trade-card trade-card--skeleton ${sponsored ? "trade-card--sponsored" : "trade-card--regular"}`}>
      <div className="trade-image-wrapper skeleton shimmer" />
      <div className="tradeCardBody">
        <div className="skeleton shimmer skeleton-title" />
        <div className="skeleton shimmer skeleton-meta" />
        <div className="skeleton shimmer skeleton-price" />
        <div className="skeleton-row">
          <div className="skeleton shimmer skeleton-pill" />
          <div className="skeleton shimmer skeleton-date" />
        </div>
      </div>
    </div>
  );
};

const TradeListings = ({
  items = null,
  trades = [],
  publicMode = false,
  publicLimit = 60,
  publicSearchTerm = "",
  searchTerm = "",
  publicCategoryKey = null,
  conditionFilter = "all",
  refreshKey = null,
  setFetchedTrades = null,
}) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(
    Array.isArray(items) ? items : Array.isArray(trades) ? trades : []
  );

  const [sponsoredTrades, setSponsoredTrades] = useState([]);
  const [urgentTrades, setUrgentTrades] = useState([]);
  const [regularTrades, setRegularTrades] = useState([]);

  const [hoveredTradeId, setHoveredTradeId] = useState(null);
  const [quickViewTrade, setQuickViewTrade] = useState(null);
  const [imageIndexes, setImageIndexes] = useState({});
  const [wishlistedIds, setWishlistedIds] = useState(() => {
    try {
      const raw = localStorage.getItem("nz_wishlist_ids");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  
  const urgentScrollRef = useRef(null);
  const sponsoredScrollRef = useRef(null);

  const toggleWishlist = useCallback((tradeId) => {
    setWishlistedIds((prev) => {
      const next = prev.includes(tradeId)
        ? prev.filter((id) => id !== tradeId)
        : [...prev, tradeId];

      try {
        localStorage.setItem("nz_wishlist_ids", JSON.stringify(next));
      } catch {}

      return next;
    });
  }, []);

  const setTradeImageIndex = useCallback((tradeId, nextIndex, imageCount) => {
    setImageIndexes((prev) => ({
      ...prev,
      [tradeId]: ((nextIndex % imageCount) + imageCount) % imageCount,
    }));
  }, []);

  const effectiveSearch = useMemo(() => {
    const primary = String(publicSearchTerm || "").trim();
    if (primary) return primary;
    return String(searchTerm || "").trim();
  }, [publicSearchTerm, searchTerm]);

  const hasExternalTrades = Array.isArray(trades) && trades.length > 0;

  const effectivePublicMode = useMemo(() => {
    if (Array.isArray(items)) return false;
    if (publicMode) return true;
    if (hasExternalTrades) return false;
    return true;
  }, [items, publicMode, hasExternalTrades]);

  useEffect(() => {
    if (typeof setFetchedTrades === "function") {
      setFetchedTrades([]);
    }
  }, [setFetchedTrades]);

  useEffect(() => {
    if (Array.isArray(items)) setRows(items);
  }, [items]);

  useEffect(() => {
    if (Array.isArray(items)) return;
    if (effectivePublicMode) return;
    setRows(Array.isArray(trades) ? trades : []);
  }, [trades, effectivePublicMode, items]);

  useEffect(() => {
    let alive = true;

    if (Array.isArray(items)) return;
    if (!effectivePublicMode) return;

    setLoading(true);

    const lim = Math.min(Number(publicLimit) || 60, 200);

    const qRef = query(
      collection(db, "PublicTrades"),
      orderBy("createdAt", "desc"),
      limit(lim)
    );

    getDocs(qRef)
      .then((snap) => {
        if (!alive) return;
        const list = snap.docs.map((d) => normalizeForUI({ id: d.id, ...d.data() }));
        setRows(list);
      })
      .catch((e) => {
        console.error("Failed to load PublicTrades:", e);
        if (!alive) return;
        setRows([]);
        if (typeof setFetchedTrades === "function") {
          setFetchedTrades([]);
        }
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [effectivePublicMode, publicLimit, publicCategoryKey, items, refreshKey, setFetchedTrades]);

  useEffect(() => {
    const input = Array.isArray(rows) ? rows : [];

    const normalized = input.map((t) => normalizeForUI(t));
    const categoryFiltered = normalized.filter((t) => matchesCategoryFilter(t, publicCategoryKey));
    const searched = categoryFiltered.filter((t) => matchesSearch(t, effectiveSearch));
    const conditioned = searched.filter((t) => matchesCondition(t, conditionFilter));
    const available = conditioned.filter(isTradeAvailable);
      
    if (typeof setFetchedTrades === "function") {
      setFetchedTrades(available);
    }

   const urgent = available.filter((t) => isUrgentTrade(t));

    // Sponsored-only row excludes urgent items because urgent items get first-row priority.
    const sponsoredOnly = available.filter(
      (t) => isSponsoredActive(t) && !isUrgentTrade(t)
    );

    // All Trades remains the full normal marketplace surface.
    const regular = available;

    setUrgentTrades(sortUrgentPriorityTrades(urgent).slice(0, 16));
    setSponsoredTrades(sortSponsoredTrades(sponsoredOnly).slice(0, 16));

    setRegularTrades(
      rankRegularTrades(regular, {
        searchTerm: effectiveSearch,
        categoryKey: publicCategoryKey || "",
      })
    );
  }, [rows, effectiveSearch, conditionFilter, publicCategoryKey, setFetchedTrades]);

 const attachAutoScroller = useCallback((scrollEl, distance = 320, intervalMs = 3200) => {
    if (!scrollEl) return () => {};

    let interval = null;

    const stop = () => {
      if (interval) clearInterval(interval);
      interval = null;
    };

    const start = () => {
      if (interval) return;

      interval = setInterval(() => {
        if (!scrollEl) return;

        scrollEl.scrollBy({ left: distance, behavior: "smooth" });

        if (scrollEl.scrollLeft + scrollEl.offsetWidth >= scrollEl.scrollWidth - 10) {
          scrollEl.scrollTo({ left: 0, behavior: "smooth" });
        }
      }, intervalMs);
    };

    scrollEl.addEventListener("mouseenter", stop);
    scrollEl.addEventListener("mouseleave", start);
    start();

    return () => {
      stop();
      scrollEl.removeEventListener("mouseenter", stop);
      scrollEl.removeEventListener("mouseleave", start);
    };
  }, []);

  useEffect(() => {
    if (urgentTrades.length === 0) return undefined;
    return attachAutoScroller(urgentScrollRef.current, 310, 2800);
  }, [urgentTrades, attachAutoScroller]);

  useEffect(() => {
    if (sponsoredTrades.length === 0) return undefined;
    return attachAutoScroller(sponsoredScrollRef.current, 320, 3300);
  }, [sponsoredTrades, attachAutoScroller]);

  const scroll = (ref, direction) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -320 : 320, behavior: "smooth" });
  }; 

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndexes((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          next[id] = next[id] + 1;
        });
        return next;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const renderTradeCard = (trade, opts = {}) => {
    const variant = opts?.variant === "sponsored" ? "sponsored" : "regular";

    const isSponsored = isSponsoredActive(trade);
    const isHighlighted = isHighlightedActive(trade);
    const isUrgent = isUrgentActive(trade);

    const primary = getPrimaryImage(trade);
    const images = getTradeImages(trade);
    const imageCount = images.length || 1;
    const currentIndex = imageIndexes[trade.id] || 0;
    const currentImage = images[currentIndex] || primary;
    const hasMultipleImages = imageCount > 1;
    const isWishlisted = wishlistedIds.includes(trade.id);
    const hasValidHttp = typeof primary === "string" && /^https?:\/\//.test(primary);
    const fallback = fallbackSvgDataUri(trade?.title || "Trade Item");
    const imageUrl = hasValidHttp ? primary : fallback;

    const prefMeta = preferenceLabel();

    const expectedCash =
      trade?.expectedCashAmount != null ? trade.expectedCashAmount : trade?.estimatedValue;

    const listed = formatDate(trade?.createdAt, variant === "sponsored" ? "short" : "long");
    const conditionKey = getConditionKey(trade);

    return (
      <Link
        to={`/trade/${trade.id}`}
        key={trade.id}
        className={`trade-card-link ${variant === "sponsored" ? "trade-card-link--sponsored" : "trade-card-link--regular"}`}
      >
        <div
          className={[
            "card",
            "trade-card",
            isSponsored ? "sponsored-trade-card" : "",
            isHighlighted ? "trade-card--highlighted" : "",
            isUrgent ? "trade-card--urgent" : "",
            variant === "sponsored" ? "trade-card--sponsored" : "trade-card--regular",
          ].join(" ")}
        >
          <div
            className="trade-image-wrapper"
            onMouseEnter={() => setHoveredTradeId(trade.id)}
            onMouseLeave={() => setHoveredTradeId((prev) => (prev === trade.id ? null : prev))}
          >
            <img
              src={currentImage || getTradeImageSrc(trade) || imageUrl}
              alt={trade?.title || "Trade"}
              onError={handleImgError}
              loading="lazy"
            />

            {(isSponsored || isHighlighted || isUrgent) && (
              <div className="tradeCardOverlayBadges">
                {isSponsored ? <span className="tradeCardBadge">Sponsored</span> : null}
                {isHighlighted ? (
                  <span className="tradeCardBadge tradeCardBadge--highlight">Highlighted</span>
                ) : null}
                {isUrgent ? (
                  <span className="tradeCardBadge tradeCardBadge--urgent">Urgent</span>
                ) : null}
              </div>
            )}

            {hasMultipleImages && hoveredTradeId === trade.id ? (
              <>
                <button
                  type="button"
                  className="tradeCardCarouselNav tradeCardCarouselNav--left"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTradeImageIndex(trade.id, currentIndex - 1, imageCount);
                  }}
                  aria-label="Previous image"
                >
                  ‹
                </button>

                <button
                  type="button"
                  className="tradeCardCarouselNav tradeCardCarouselNav--right"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTradeImageIndex(trade.id, currentIndex + 1, imageCount);
                  }}
                  aria-label="Next image"
                >
                  ›
                </button>

                <div className="tradeCardDots">
                  {images.slice(0, 5).map((_, idx) => (
                    <span
                      key={idx}
                      className={`tradeCardDot ${idx === currentIndex ? "isActive" : ""}`}
                    />
                  ))}
                </div>
              </>
            ) : null}

            <div className={`tradeCardQuickActions ${
              hoveredTradeId === trade.id || isTouchDevice ? "isVisible" : ""
            }`}>
              <button
                type="button"
                className={`tradeCardQuickBtn ${isWishlisted ? "isActive" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWishlist(trade.id);
                }}
              >
                {isWishlisted ? "Saved" : "Save"}
              </button>

              <button
                type="button"
                className="tradeCardQuickBtn tradeCardQuickBtn--primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuickViewTrade(trade);
                }}
              >
                Preview
              </button>
            </div>
          </div>

          <div className="card-body tradeCardBody">
            <div className="tradeCardPriceRow">
              {moneyZAR(expectedCash) ? (
                <span className="tradeCardPriceMain">
                  {moneyZAR(expectedCash)}
                </span>
              ) : (
                <span className="tradeCardPriceMain tradeCardPriceMain--muted">
                  Price on request
                </span>
              )}

              <span className={`tradeCardPref ${prefMeta.tone}`}>
                Cash
              </span>
            </div>

            <div className="tradeCardTitle" title={trade?.title || ""}>
              {trade?.title || "Untitled"}
            </div>

            <div className="tradeCardSignals">
              <span className="tradeSignal">
                {trade?.viewCount ?? 0} views
              </span>

              <span className="tradeSignal">
                {trade?.favouriteCount ?? 0} saves
              </span>
            </div>

            <div className="tradeCardBottom">
              <div
                className={`tradeCardCondition ${
                  conditionKey === "new" ? "isNew" : "isUsed"
                }`}
              >
                {conditionKey === "new" ? "New" : "Pre-owned"}
              </div>

              <div className="tradeCardBottomRight">
                {!!listed && <div className="tradeCardDate">{listed}</div>}
              </div>
            </div>

            <div className="tradeCardCTA">
              View details
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const empty =
  !loading &&
  sponsoredTrades.length === 0 &&
  urgentTrades.length === 0 &&
  regularTrades.length === 0;

  return (
    <div className="trade-listings-container">
      {loading ? (
        <>
          <div className="sponsored-section">
            <div className="sponsored-section__header">
              <div className="sponsored-section__titleWrap">
                <h3 className="sponsored-section__title">Sponsored Trades</h3>
              </div>
            </div>

            <div className="carousel-container">
              <div className="sponsored-scroll">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="sponsored-anim">
                    <TradeCardSkeleton sponsored />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="regular-section">
            <h3 className="regular-section__title">All Trades</h3>
            <div className="regular-grid">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div className="regular-col" key={idx}>
                  <TradeCardSkeleton />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : empty ? (
        <p className="text-center mt-4">No trades found. Be the first to post one.</p>
      ) : (
        <>
          {urgentTrades.length > 0 && (
            <div className="urgent-section">
              <div className="urgent-section__header">
                <div className="urgent-section__titleWrap">
                  <h3 className="urgent-section__title">Quick Trades</h3>
                  <span className="urgent-section__pill">Sponsored first</span>
                </div>

                <p className="urgent-section__subtitle">
                  Fast-moving listings. Sponsored urgent trades appear first.
                </p>
              </div>

              <div className="urgent-carousel-container">
                <button
                  className="scroll-arrow left urgent-scroll-arrow"
                  onClick={() => scroll(urgentScrollRef, "left")}
                  aria-label="Scroll urgent trades left"
                  type="button"
                >
                  &lt;
                </button>

                <div className="urgent-scroll" ref={urgentScrollRef}>
                  {urgentTrades.map((trade, idx) => (
                    <div
                      key={trade.id}
                      className="urgent-anim"
                      style={sponsoredAnimStyle(idx)}
                    >
                      {renderTradeCard(trade, { variant: "regular" })}
                    </div>
                  ))}
                </div>

                <button
                  className="scroll-arrow right urgent-scroll-arrow"
                  onClick={() => scroll(urgentScrollRef, "right")}
                  aria-label="Scroll urgent trades right"
                  type="button"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}

          {sponsoredTrades.length > 0 && (
            <div className="sponsored-section">
              <div className="sponsored-section__header">
                <div className="sponsored-section__titleWrap">
                  <h3 className="sponsored-section__title">Sponsored Trades</h3>

                  <span
                    className="sponsored-section__pill"
                    role="note"
                    aria-label="Why am I seeing this?"
                  >
                    Featured
                    <span
                      className="sponsored-section__help"
                      tabIndex={0}
                      aria-label="Why am I seeing this?"
                    >
                      ?
                      <span className="sponsored-section__tooltip" role="tooltip">
                        Sponsored items appear here because sellers paid to promote their listings.
                        You can promote yours too to reach more buyers.
                      </span>
                    </span>
                  </span>
                </div>
              </div>

              <div className="sponsored-section__divider" />

              <div className="carousel-container">
                <button
                  className="scroll-arrow left"
                  onClick={() => scroll(sponsoredScrollRef, "left")}
                  aria-label="Scroll left"
                  type="button"
                >
                  &lt;
                </button>

               
                <div className="sponsored-scroll" ref={sponsoredScrollRef}>
                  {sponsoredTrades.map((trade, idx) => (
                    <div key={trade.id} className="sponsored-anim" style={sponsoredAnimStyle(idx)}>
                      {renderTradeCard(trade, { variant: "sponsored" })}
                    </div>
                  ))}
                </div>

                <button
                  className="scroll-arrow right"
                  onClick={() => scroll(sponsoredScrollRef, "right")}
                  aria-label="Scroll right"
                  type="button"
                >
                  &gt;
                </button>
              </div>

              <div className="sponsored-cta">
                <div className="sponsored-cta__text">
                  <div className="sponsored-cta__title">Want your item featured here?</div>
                  <div className="sponsored-cta__sub">
                    Promote your listing and get more views, faster offers, and better matches.
                  </div>
                </div>

                <button
                  type="button"
                  className="sponsored-cta__btn"
                  onClick={() => navigate("/market/post-trade")}
                >
                  Promote an item
                </button>
              </div>
            </div>
          )}

          <div className="regular-section">
            <h3 className="regular-section__title">All Trades</h3>

            {regularTrades.length > 0 ? (
              <div className="regular-grid">
                {regularTrades.map((trade) => (
                  <div className="regular-col" key={trade.id}>
                    {renderTradeCard(trade, { variant: "regular" })}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted text-center">No trade listings available yet.</p>
            )}
          </div>
        </>
      )}

      {quickViewTrade ? (
        <div
          className="tradePreviewModal"
          onClick={() => setQuickViewTrade(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="tradePreviewModal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="tradePreviewModal__close"
              onClick={() => setQuickViewTrade(null)}
              aria-label="Close preview"
            >
              ×
            </button>

            <div className="tradePreviewModal__media">
              <img
                src={getTradeImages(quickViewTrade)[0] || getTradeImageSrc(quickViewTrade)}
                alt={quickViewTrade?.title || "Preview"}
                onError={handleImgError}
              />
            </div>

            <div className="tradePreviewModal__body">
              <h3>{quickViewTrade?.title || "Untitled"}</h3>
              <p>
                {String(quickViewTrade?.description || "No description provided.").trim()}
              </p>

              <div className="tradePreviewModal__meta">
                <span className="tradeCardPref tradepref-cash">Cash offer</span>

                {moneyZAR(
                  quickViewTrade?.expectedCashAmount ??
                  quickViewTrade?.estimatedValue ??
                  quickViewTrade?.price
                ) ? (
                  <span className="tradeCardPriceChip isCashOnly">
                    <span className="tradeCardPriceLabel">Price</span>
                    <span className="tradeCardPriceValue">
                      {moneyZAR(
                        quickViewTrade?.expectedCashAmount ??
                        quickViewTrade?.estimatedValue ??
                        quickViewTrade?.price
                      )}
                    </span>
                  </span>
                ) : (
                  <span className="tradeCardHint">Price on request</span>
                )}
              </div>

              <Link
                to={`/trade/${quickViewTrade.id}`}
                className="tradePreviewModal__cta"
                onClick={() => setQuickViewTrade(null)}
              >
                View full listing
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TradeListings;